import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { merchantService } from '../services/merchantService';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Building2, Download, Search } from 'lucide-react';

const Companies = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    merchantService
      .getAll()
      .then(setCompanies)
      .catch((apiError) => setError(apiError.userMessage || 'Unable to load companies.'))
      .finally(() => setLoading(false));
  }, []);

  const filteredCompanies = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) {
      return companies;
    }

    return companies.filter((company) =>
      [
        company.orgName,
        company.orgId?.toString(),
        company.orgEmail,
        company.city,
        company.state,
        company.country,
      ].some((value) => String(value || '').toLowerCase().includes(query))
    );
  }, [companies, searchQuery]);

  const countries = new Set(companies.map((company) => company.country).filter(Boolean)).size;
  const states = new Set(companies.map((company) => company.state).filter(Boolean)).size;
  const cities = new Set(companies.map((company) => company.city).filter(Boolean)).size;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Companies</h1>
          <p className="text-gray-500 mt-1">Companies using ApurvaX Payroll services</p>
        </div>
        <Button variant="outline">
          <Download className="h-4 w-4" />
          Export
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Total Companies</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{companies.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Countries</p>
            <p className="text-2xl font-bold text-green-600 mt-1">{countries}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">States</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{states}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Cities</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{cities}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-6">
            <div className="relative w-full md:max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search companies..."
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="pl-10"
              />
            </div>
            <p className="text-sm text-gray-500">{filteredCompanies.length} companies found</p>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Company ID</TableHead>
                  <TableHead>Legal Name</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Country</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCompanies.map((company) => (
                  <TableRow
                    key={company.orgId}
                    className="cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => navigate(`/settings?companyId=${company.orgId}`, { state: { company } })}
                  >
                    <TableCell>
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center">
                          <Building2 className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{company.orgName}</p>
                          <p className="text-sm text-gray-500">{company.orgEmail}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{company.orgId}</TableCell>
                    <TableCell>{company.legalName || '-'}</TableCell>
                    <TableCell>{company.city || '-'}</TableCell>
                    <TableCell>{company.state || '-'}</TableCell>
                    <TableCell>{company.country || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {loading && <p className="mt-4 text-sm text-gray-500">Loading companies...</p>}
          {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
        </CardContent>
      </Card>
    </div>
  );
};

export default Companies;
